import type { CaseStatus, ChatSubjectKind, QuoteStatus } from '@/types/domain'

/* What a RECORD contributes to its conversation's row (0052).
 *
 * The conversation wire carries a subject pair — a kind and an id — and nothing
 * human: no number, no client, no lifecycle state. Those are joined in on the
 * client from the two lists the reader can already see, and this module is the
 * shape of that join plus the one function that keys it.
 *
 * Its own file rather than exports hanging off ConversationList, for the reason
 * lib/statusTone gives: a component module that also exports plain functions
 * loses fast refresh, and the map is built by the page while the rows are
 * rendered by the list — so neither of them is the natural owner anyway.
 */

export interface RecordLabel {
  number: string
  clientName: string
  /** The record's lifecycle state, for the row's chip.
   *
   *  Discriminated by KIND rather than widened to `CaseStatus | QuoteStatus`,
   *  because the pill is picked from it: a bare union would need a cast at the
   *  render site, and `draft` is a member of BOTH — so the cast would compile
   *  happily while colouring a quote from the case table. */
  status?:
    | { kind: 'case'; value: CaseStatus }
    | { kind: 'quote'; value: QuoteStatus }
}

export type RecordLabels = Map<string, RecordLabel>

/** The key the map is written and read with. One function rather than two
 *  template literals at either end, so a change of separator cannot leave the
 *  writer and the reader disagreeing silently — which would show up as every
 *  row falling back to the id's head. */
export function labelKey(kind: ChatSubjectKind, subjectId: string): string {
  return `${kind}:${subjectId}`
}
